import { useEffect, useState } from 'react';
import { ArrowUpCircle, Download, X } from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'dutilyne.updateBannerDismissed';

const isNewer = (candidate, current) => {
  const a = String(candidate || '').split('.').map((n) => parseInt(n, 10) || 0);
  const b = String(current || '').split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0);
  }
  return false;
};

/** Shows inside the Windows terminal when the app-hosted installer is newer than this install. */
export const DesktopUpdateBanner = () => {
  const bridge = typeof window !== 'undefined' ? window.pharmxai : null;
  const [installed, setInstalled] = useState('');
  const [latest, setLatest] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!bridge?.getAppVersion) return;
    (async () => {
      try {
        const [{ version }, { data }] = await Promise.all([
          bridge.getAppVersion(),
          api.get('/desktop-app/latest'),
        ]);
        setInstalled(version || '');
        setLatest(data?.available ? data : null);
        setDismissed(localStorage.getItem(DISMISS_KEY) === data?.version);
      } catch {
        setLatest(null);
      }
    })();
  }, [bridge]);

  if (!latest || dismissed || !isNewer(latest.version, installed)) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, latest.version);
    setDismissed(true);
  };

  return (
    <div
      data-testid="desktop-update-banner"
      className="relative flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 sm:flex-row sm:items-center"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100">
        <ArrowUpCircle className="h-5 w-5 text-amber-700" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-amber-900">
          New version available — v{latest.version}
        </p>
        <p className="mt-0.5 text-xs text-amber-800" data-testid="desktop-update-banner-meta">
          This terminal is on v{installed}. Download the update, close DutiLyne and run the file.
          Sync any queued sales first — then nothing is lost.
        </p>
      </div>
      <Button asChild className="shrink-0 gap-2 bg-amber-600 hover:bg-amber-700" data-testid="desktop-update-download-btn">
        <a href={`${API_URL}/api/desktop-app/download`} download>
          <Download className="h-4 w-4" /> Download update
        </a>
      </Button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Hide this update notice"
        data-testid="desktop-update-dismiss-btn"
        className="absolute right-2 top-2 rounded-md p-1 text-amber-700 transition-colors hover:bg-amber-100 sm:static sm:self-center"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
